import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import BuilderDateField from './BuilderDateField'

type Props = {
  value: string
  onChange: (iso: string) => void
  max?: string
  ariaLabel: string
  preferAbove?: boolean
}

function thumbCapIndex(activeIndex: number, count: number): number {
  if (count <= 1 || activeIndex <= 0) return 0
  if (activeIndex >= count - 1) return 3
  return 2
}

/** Date + “Nežinoma” in the same pill bar as warranty / battery origin segments. */
export default function BuilderBatteryTestedField({
  value,
  onChange,
  max,
  ariaLabel,
  preferAbove = false,
}: Props) {
  const { t } = useTranslation()
  const unknown = !value.trim()
  const activeIndex = unknown ? 1 : 0
  const thumbCap = thumbCapIndex(activeIndex, 2)

  const groupRef = useRef<HTMLDivElement>(null)
  const dateSlotRef = useRef<HTMLDivElement>(null)
  const unknownRef = useRef<HTMLButtonElement>(null)
  const [thumbRect, setThumbRect] = useState({ width: 0, left: 0 })

  const syncThumb = useCallback(() => {
    const group = groupRef.current
    const target = activeIndex === 0 ? dateSlotRef.current : unknownRef.current
    if (!group || !target) return
    const groupBox = group.getBoundingClientRect()
    const targetBox = target.getBoundingClientRect()
    setThumbRect({
      width: targetBox.width,
      left: targetBox.left - groupBox.left,
    })
  }, [activeIndex])

  useLayoutEffect(() => {
    syncThumb()
  }, [syncThumb, value, unknown])

  useEffect(() => {
    const group = groupRef.current
    if (!group || typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(() => syncThumb())
    ro.observe(group)
    window.addEventListener('resize', syncThumb)
    return () => {
      ro.disconnect()
      window.removeEventListener('resize', syncThumb)
    }
  }, [syncThumb])

  return (
    <div className="builder-history-segments parts-auth-form--builder-detail builder-battery-tested-field">
      <div
        ref={groupRef}
        className="parts-auth-form__segmented builder-battery-tested-field__bar"
        data-segment-active={activeIndex}
      >
        <span
          className={`parts-auth-form__segment-thumb parts-auth-form__segment-thumb--pos-${thumbCap}`}
          aria-hidden
          style={{
            width: thumbRect.width > 0 ? `${thumbRect.width}px` : undefined,
            transform: `translateX(${thumbRect.left}px)`,
          }}
        />
        <div
          ref={dateSlotRef}
          className={`builder-battery-tested-field__date${unknown ? ' builder-battery-tested-field__date--inactive' : ''}`}
        >
          <BuilderDateField
            layout="segment"
            value={value}
            onChange={onChange}
            max={max}
            ariaLabel={ariaLabel}
            preferAbove={preferAbove}
          />
        </div>
        <button
          ref={unknownRef}
          type="button"
          role="radio"
          aria-checked={unknown}
          className={`parts-auth-form__segment${unknown ? ' parts-auth-form__segment--on' : ''}`}
          onClick={() => onChange('')}
        >
          <span className="parts-auth-form__segment-text">
            {t('batteryTransparency.form.originUnknown')}
          </span>
        </button>
      </div>
    </div>
  )
}
